import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingUp, TrendingDown, Minus, BookOpen, ChevronDown, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { tone } from "@/components/PredictionScore";
import { GaugeInfoIcon, GaugePopover, gaugeExplainers } from "@/components/dashboard/GaugeInfo";
import { HeroGauge } from "@/components/dashboard/HeroGauge";
import { ScoreTrend } from "@/components/dashboard/ScoreTrend";

// Dashboard centrepiece after the EV-IDEX reference art: big glowing main gauge card, three horizontal supporting cards
// (ring left; title → score/status → eligible-call count → brief note on the right; ⓘ), "How scoring works", then update
// time + full-methodology link. Same /prediction-score data and calculations as the Performance tab; every card is a
// hover / focus / tap pop-up trigger. Thresholds are never hard-coded: threshold = graded count + still-needed from the API.

const Num = ({ v }) => <span className="text-cyan-300 font-semibold">{v}</span>;

const Ring = ({ pct, tid, size = 84 }) => {
  const r = 38, c = 2 * Math.PI * r, f = pct == null ? 0 : Math.max(0, Math.min(100, pct)) / 100, col = pct == null ? "#334155" : tone(pct);
  return (
    <svg viewBox="0 0 100 100" style={{ width: size, height: size }} className="shrink-0" data-testid={`${tid}-gauge`}>
      <circle cx="50" cy="50" r={r} fill="none" stroke="#1E293B" strokeWidth="10" />
      <circle cx="50" cy="50" r={r} fill="none" stroke={col} strokeWidth="10" strokeLinecap="round" transform="rotate(-90 50 50)"
        strokeDasharray={`${c} ${c}`} strokeDashoffset={c * (1 - f)} style={{ transition: "stroke-dashoffset 900ms ease", filter: pct == null ? "none" : `drop-shadow(0 0 7px ${col}99)` }} />
      <text x="50" y="58" textAnchor="middle" fill="#F8FAFC" fontSize="24" fontWeight="700" className="font-mono-ticker" data-testid={`${tid}-value`}>{pct == null ? "—" : `${Math.round(pct)}%`}</text>
    </svg>
  );
};

const Pill = ({ status, tid }) => (
  <span className={`text-[10px] font-mono-ticker uppercase tracking-widest px-2 py-0.5 rounded-full border ${status === "preliminary" ? "border-amber-500/40 text-amber-300" : "border-emerald-500/40 text-emerald-300"}`} data-testid={tid}>{status}</span>
);

// Progress to publication: live counts against the current thresholds; status pill once the score is published.
const Progress = ({ cat }) => {
  if (cat.status !== "insufficient") return <Pill status={cat.status} tid="gauge-main-status" />;
  const dirN = cat.directional?.n ?? 0;
  return (
    <div className="w-full max-w-[300px]" data-testid="gauge-main-progress">
      <div className="text-[10px] font-mono-ticker uppercase tracking-[0.2em] text-slate-500 mb-2">Progress to publication</div>
      <dl className="grid grid-cols-[1fr_auto] gap-x-6 gap-y-1 text-[13px] sm:text-sm text-left">
        <dt className="text-slate-300">Independent calls</dt>
        <dd className="font-mono-ticker text-slate-50 tabular-nums" data-testid="gauge-main-progress-independent"><Num v={cat.resolved} /> / {cat.resolved + cat.needed.resolved}</dd>
        <dt className="text-slate-300">Directional calls</dt>
        <dd className="font-mono-ticker text-slate-50 tabular-nums" data-testid="gauge-main-progress-directional"><Num v={dirN} /> / {dirN + cat.needed.directional}</dd>
      </dl>
      <p className="mt-2 text-[12px] text-slate-400 leading-relaxed" data-testid="gauge-main-progress-note">Both thresholds must be met before this score is published. Counts update after grading.</p>
    </div>
  );
};

// Previous week's published score (if any) and the change since, kept to one short line.
const PreviousWeek = ({ prev, change }) => {
  const Icon = change == null ? Minus : change > 0 ? TrendingUp : TrendingDown;
  return (
    <p className="flex items-center justify-center gap-1.5 text-[11px] sm:text-xs font-mono-ticker text-slate-400" data-testid="gauge-main-previous-week">
      <Icon className={`w-3.5 h-3.5 ${change == null ? "text-slate-500" : change > 0 ? "text-emerald-400" : change < 0 ? "text-red-400" : "text-slate-400"}`} />
      {prev == null ? "Previous week: no published score." : <>Previous week: {prev} / 100{change != null && <> · {change > 0 ? "+" : ""}{change} since</>}</>}
    </p>
  );
};

const cardStyle = (color) => ({ borderColor: `${color}80`, background: `linear-gradient(135deg, ${color}1f 0%, rgba(13,19,28,0.96) 60%)`, boxShadow: `0 0 22px ${color}1a, inset 0 0 0 1px ${color}10` });

const SideCard = ({ item, tid, color, gauge, label, labelClass = "text-cyan-200", value, count, note }) => (
  <GaugePopover item={item} tid={tid} side="bottom" style={cardStyle(color)} className="relative rounded-2xl border p-3 sm:p-4 transition-transform duration-200 hover:-translate-y-0.5">
    <div className="flex items-center gap-3 sm:gap-4 w-full">
      {gauge}
      <div className="min-w-0 flex-1 text-left">
        <div className={`text-[13px] font-semibold tracking-wide ${labelClass}`}>{label}</div>
        <div className="mt-0.5 text-[15px] font-mono-ticker text-slate-50 leading-tight" data-testid={`${tid}-value-line`}>{value}</div>
        <div className="mt-1 text-[12px] sm:text-[13px] text-slate-300 leading-snug" data-testid={`${tid}-count`}>{count}</div>
        <div className="text-[12px] sm:text-[13px] text-slate-400 leading-snug" data-testid={`${tid}-note`}>{note}</div>
      </div>
      <GaugeInfoIcon tid={`${tid}-info`} className="self-start" />
    </div>
  </GaugePopover>
);

const pct = (a) => (a?.accuracy == null ? "—" : `${a.accuracy}%`);
const ci = (a) => (a?.wilson_ci_95 ? `95% CI ${a.wilson_ci_95[0]}–${a.wilson_ci_95[1]}%` : "95% CI not yet available");
const elig = (a) => <><Num v={a?.correct ?? 0} /> of <Num v={a?.n ?? 0} /> eligible calls correct</>;

const HowScoring = ({ ex }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-2xl border border-slate-700/70 bg-[#0D131C]" data-testid="gauges-how-scoring">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} className="w-full px-4 py-3.5 flex items-center gap-3 text-base text-slate-100 hover:text-cyan-100 transition-colors" data-testid="gauges-how-scoring-toggle">
        <BookOpen className="w-5 h-5 text-slate-300" /> <span className="font-display flex-1 text-left">How scoring works</span>
        <ChevronDown className={`w-5 h-5 text-slate-300 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 text-[13px] text-slate-300 leading-relaxed space-y-2" data-testid="gauges-how-scoring-body">
          <p>{ex.main.what}</p>
          <p>{ex.main.how}</p>
          <Link to="/performance?tab=score" className="inline-block text-cyan-300 hover:underline" data-testid="gauges-how-scoring-link">Full score, ledger &amp; methodology →</Link>
        </div>
      )}
    </div>
  );
};

export const DashboardGauges = () => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let alive = true;
    const load = () => api.get("/prediction-score").then((r) => alive && setData(r.data)).catch(() => alive && setErr(true));
    load();
    const t = setInterval(load, 60000);
    const onVis = () => document.visibilityState === "visible" && load();
    document.addEventListener("visibilitychange", onVis);
    return () => { alive = false; clearInterval(t); document.removeEventListener("visibilitychange", onVis); };
  }, []);
  if (err) return null;
  if (!data) return <div className="apx-card skeleton h-72" data-testid="gauges-loading" />;
  const m = data.current.market, s = data.current.strategy;
  const mEmpty = m.status === "insufficient", sEmpty = s.status === "insufficient";
  const sDir = s.directional?.n ?? 0;
  const updated = new Date(data.current.computed_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  const ex = gaugeExplainers(data);
  return (
    <section className="relative" data-testid="dashboard-gauges">
      <div className="grid grid-cols-[minmax(0,1fr)] gap-4 xl:grid-cols-[1.25fr_1fr] items-start">
        <GaugePopover item={ex.main} tid="gauge-main" side="right" className="relative overflow-hidden rounded-2xl border border-cyan-500/60 p-4 sm:p-6 flex flex-col items-center text-center"
          style={{ background: "radial-gradient(130% 120% at 50% 0%, rgba(34,211,238,0.18) 0%, rgba(11,15,23,0) 60%), #0B1119", boxShadow: "0 0 40px rgba(34,211,238,0.12), inset 0 0 0 1px rgba(34,211,238,0.08)" }}>
          <div className="w-full flex items-center justify-between gap-3">
            <span className="w-7" aria-hidden="true" />
            <div className="text-[14px] sm:text-lg font-mono-ticker uppercase tracking-[0.16em] text-cyan-300 leading-tight">EV-IDEX Prediction Accuracy Score</div>
            <GaugeInfoIcon tid="gauge-main-info" className="w-7 h-7 min-w-[28px]" />
          </div>
          <div className="mt-2 w-full flex justify-center" data-testid="gauge-main-hit">
            <HeroGauge score={mEmpty ? 0 : m.score} empty={mEmpty} tid="gauge-main" className="w-[440px] max-w-full h-auto" />
          </div>
          <div className="mt-3 pt-3 w-full border-t border-cyan-900/60 flex flex-col items-center"><Progress cat={m} /></div>
          <div className="mt-3 pt-2 w-full border-t border-cyan-900/40"><PreviousWeek prev={data.previous?.market?.score ?? null} change={data.change?.market} /></div>
          <div className="w-full flex justify-center px-2 empty:hidden"><ScoreTrend data={data} /></div>
        </GaugePopover>

        <div className="flex flex-col gap-3">
          <SideCard item={ex.directional} tid="gauge-directional" color={m.directional?.accuracy == null ? "#64748b" : tone(m.directional.accuracy)} label="Directional Accuracy"
            gauge={<Ring pct={m.directional?.accuracy} tid="gauge-directional" />} value={pct(m.directional)} count={elig(m.directional)} note={ci(m.directional)} />
          <SideCard item={ex.nondirectional} tid="gauge-nondirectional" color={m.non_directional?.accuracy == null ? "#64748b" : tone(m.non_directional.accuracy)} label="Non-Directional Accuracy"
            gauge={<Ring pct={m.non_directional?.accuracy} tid="gauge-nondirectional" />} value={pct(m.non_directional)} count={elig(m.non_directional)} note="WAIT/Neutral calls only" />
          <SideCard item={ex.strategy} tid="gauge-strategy" color="#6D5BD0" label="Strategy Outlook" labelClass="text-violet-300"
            gauge={<HeroGauge score={sEmpty ? 0 : s.score} empty={sEmpty} tid="gauge-strategy" mini className="w-[96px] sm:w-[124px] h-auto shrink-0" />}
            value={sEmpty ? "Insufficient data" : <>{s.score} / 100 <Pill status={s.status} tid="gauge-strategy-status" /></>}
            count={<span className="inline-flex flex-wrap gap-x-3 gap-y-0.5"><span className="whitespace-nowrap"><Num v={s.resolved} /> / {s.resolved + s.needed.resolved} independent</span><span className="whitespace-nowrap"><Num v={sDir} /> / {sDir + s.needed.directional} directional</span></span>}
            note="BUY / SELL / AVOID calls" />
          <HowScoring ex={ex} />
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-[11px] font-mono-ticker text-slate-500">
        <span data-testid="gauges-updated">Updated {updated} · recomputed daily after grading</span>
        <Link to="/performance?tab=score" className="inline-flex items-center gap-1 text-cyan-300 hover:underline" data-testid="gauges-performance-link"><ShieldCheck className="w-3.5 h-3.5" /> Full score, ledger &amp; methodology →</Link>
      </div>
    </section>
  );
};
